import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAssets,
  uploadMedia,
  deleteAsset,
  importExistingAssets,
} from "../lib/settingsApi";

// Build the absolute public URL an asset resolves to. The backend returns
// `/api/media/{id}` (a relative path), which is fine for <img src> inside
// the app but not useful when the admin clicks "Copy URL" to paste into
// external tools. We prepend REACT_APP_BACKEND_URL so the pasted URL works
// anywhere.
const absoluteUrl = (relative) => {
  if (!relative) return "";
  if (/^https?:\/\//i.test(relative)) return relative;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${relative}`;
};

const formatBytes = (n) => {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function AssetLibrary({ token, onSaveMessage }) {
  const [assets, setAssets] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState({ done: 0, total: 0 });
  const [preview, setPreview] = useState(null); // asset shown in lightbox
  const [copiedId, setCopiedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Document-level ESC listener while the preview lightbox is open —
  // the modal <div>'s own onKeyDown never fires because focus lands on
  // the underlying grid button, not the overlay. Listening on document
  // captures the key regardless of focus.
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPreview(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [preview]);

  // Refetch on mount so the grid always reflects the current library.
  const reload = useCallback(() => {
    fetchAssets(token)
      .then((list) => {
        setAssets(list || []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [token]);

  // First-load auto-import: on the very first mount per browser session,
  // silently run import-from-portfolio. Idempotent on the backend, so
  // this only inserts rows the first time any admin opens the library
  // in a fresh deployment (production self-heals) and is a no-op after.
  // Marker is sessionStorage so a hard-refresh re-checks but a tab
  // switch inside the same session doesn't spam the endpoint.
  const AUTO_IMPORT_KEY = "asset-library-auto-import-v1";
  useEffect(() => {
    if (!token) return;
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(AUTO_IMPORT_KEY) === "done") return;
    let cancelled = false;
    (async () => {
      try {
        const result = await importExistingAssets(token);
        if (cancelled) return;
        window.sessionStorage.setItem(AUTO_IMPORT_KEY, "done");
        const imported = result?.imported || 0;
        const deduped = result?.deduped || 0;
        if (imported > 0 || deduped > 0) {
          reload();
          const parts = [];
          if (imported > 0)
            parts.push(`Imported ${imported} portfolio image${imported === 1 ? "" : "s"}`);
          if (deduped > 0)
            parts.push(`removed ${deduped} duplicate${deduped === 1 ? "" : "s"}`);
          onSaveMessage?.(parts.join(" · "));
        }
      } catch {
        /* silent — admin can still hit the Import button manually */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, reload, onSaveMessage]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Filter by filename (case-insensitive substring). Trimmed empty query
  // means "show everything" so the search box doesn't accidentally hide the
  // library on first focus.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((a) => (a.filename || "").toLowerCase().includes(q));
  }, [assets, query]);

  // Multi-file upload — uploads run sequentially so we can show a
  // "3 of 7" progress toast and so a single failure doesn't cascade.
  const handleFiles = useCallback(
    async (files) => {
      const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
      if (list.length === 0) return;
      setUploading(true);
      setUploadCount({ done: 0, total: list.length });
      const uploaded = [];
      for (let i = 0; i < list.length; i += 1) {
        try {
          const asset = await uploadMedia(list[i], token);
          uploaded.push({
            id: asset.id,
            url: asset.url,
            filename: asset.filename || list[i].name,
            content_type: asset.content_type,
            size: asset.size,
            width: asset.width,
            height: asset.height,
            created_at: new Date().toISOString(),
          });
        } catch (err) {
          onSaveMessage?.(
            err?.response?.data?.detail || `Upload failed on ${list[i].name}`
          );
        }
        setUploadCount({ done: i + 1, total: list.length });
      }
      // Prepend newest first, matching the backend's sort order.
      if (uploaded.length) {
        setAssets((prev) => [...uploaded, ...prev]);
        onSaveMessage?.(
          uploaded.length === 1
            ? `Uploaded ${uploaded[0].filename}`
            : `Uploaded ${uploaded.length} images`
        );
      }
      setUploading(false);
    },
    [token, onSaveMessage]
  );

  // Scan the portfolio source + DB overrides and register any images not
  // already in the library. Safe to run repeatedly — the backend dedupes
  // on URL, so nothing gets copied twice and existing URLs are preserved.
  const handleImportExisting = async () => {
    setImporting(true);
    try {
      const result = await importExistingAssets(token);
      const { imported = 0, skipped = 0, total = 0 } = result || {};
      if (imported > 0) {
        reload();
        onSaveMessage?.(
          `Imported ${imported} image${imported === 1 ? "" : "s"} from the portfolio` +
            (skipped ? ` (${skipped} already in library)` : "")
        );
      } else if (total > 0) {
        onSaveMessage?.(`Library already contains all ${total} referenced images`);
      } else {
        onSaveMessage?.("No referenced images found to import");
      }
    } catch (err) {
      onSaveMessage?.(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleCopyUrl = async (asset) => {
    const url = absoluteUrl(asset.url);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(asset.id);
      setTimeout(() => setCopiedId((id) => (id === asset.id ? null : id)), 1500);
    } catch {
      onSaveMessage?.("Copy failed — clipboard unavailable");
    }
  };

  const handleDelete = async (asset) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${asset.filename}"? This can't be undone.`)) return;
    setDeletingId(asset.id);
    try {
      await deleteAsset(asset.id, token);
      setAssets((prev) => prev.filter((a) => a.id !== asset.id));
      onSaveMessage?.(`Deleted ${asset.filename}`);
      if (preview?.id === asset.id) setPreview(null);
    } catch (err) {
      onSaveMessage?.(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  // Drag & drop handlers on the grid container itself so the admin can
  // drop anywhere in the library. `dragOver` toggles a ring so the drop
  // zone is obvious mid-drag.
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  return (
    <div data-testid="asset-library" className="space-y-6">
      {/* Header — visible count so the admin can verify the library
          reflects everything the portfolio references. */}
      <div className="flex items-baseline justify-between gap-3">
        <h2
          data-testid="asset-library-heading"
          className="font-serif text-[22px] md:text-[26px] text-ink"
        >
          Asset Library
          {loaded && (
            <span
              data-testid="asset-library-count"
              className="ml-3 font-sans text-[11px] tracking-[0.28em] uppercase text-ink/60"
            >
              — {assets.length} asset{assets.length === 1 ? "" : "s"}
            </span>
          )}
        </h2>
      </div>

      {/* Toolbar row */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by filename…"
          data-testid="asset-search"
          className="flex-1 px-4 py-2.5 font-sans text-[13px] border border-ink/20 focus:border-ink outline-none bg-paper"
        />
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid="asset-file-input"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={handleImportExisting}
            disabled={importing || uploading}
            data-testid="asset-import-button"
            title="Register every image already used in the portfolio into the library"
            className="border border-ink/25 text-ink px-4 py-2.5 font-sans text-[10px] tracking-[0.32em] uppercase disabled:opacity-40 hover:border-ink transition-colors"
          >
            {importing ? "Importing…" : "Import from portfolio"}
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="asset-upload-button"
            className="bg-ink text-paper px-5 py-2.5 font-sans text-[10px] tracking-[0.32em] uppercase disabled:opacity-40 hover:opacity-90 transition-opacity"
          >
            {uploading
              ? `Uploading ${uploadCount.done}/${uploadCount.total}…`
              : "+ Upload images"}
          </button>
        </div>
      </div>

      {/* Drop zone container. Grid + empty-state live inside so the drop
          works anywhere in the section. */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid="asset-drop-zone"
        className={`relative rounded-[2px] ${
          dragOver ? "ring-2 ring-ink/60 ring-offset-2 ring-offset-paper" : ""
        }`}
      >
        {!loaded ? (
          <div className="font-sans text-[13px] text-ink/50 py-8">Loading library…</div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="asset-empty-state"
            className="border border-dashed border-ink/25 py-16 px-6 text-center font-sans text-[13px] text-ink/50 leading-relaxed"
          >
            {query.trim() ? (
              <>No images match &ldquo;{query}&rdquo;.</>
            ) : (
              <>
                No images yet. Click <em className="not-italic text-ink/80">+ Upload images</em>
                {" "}or drag files anywhere in this panel to add them.
              </>
            )}
          </div>
        ) : (
          <ul
            data-testid="asset-grid"
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
          >
            {filtered.map((a) => (
              <li
                key={a.id}
                data-testid={`asset-${a.id}`}
                className="group border border-ink/10 hover:border-ink/40 transition-colors bg-paper"
              >
                <button
                  type="button"
                  onClick={() => setPreview(a)}
                  data-testid={`asset-${a.id}-open`}
                  className="block w-full aspect-square overflow-hidden bg-ink/5 relative"
                  aria-label={`Preview ${a.filename}`}
                >
                  <img
                    src={a.url}
                    alt={a.filename}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </button>
                <div className="p-2.5 space-y-2">
                  <div>
                    <div
                      className="font-sans text-[11px] text-ink truncate"
                      title={a.filename}
                      data-testid={`asset-${a.id}-filename`}
                    >
                      {a.filename}
                    </div>
                    <div className="font-sans text-[10px] tracking-[0.18em] uppercase text-ink/50">
                      {a.width && a.height ? `${a.width}×${a.height}` : "— × —"}
                      {a.size ? ` · ${formatBytes(a.size)}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-1">
                    <button
                      type="button"
                      onClick={() => handleCopyUrl(a)}
                      data-testid={`asset-${a.id}-copy`}
                      className="flex-1 text-left font-sans text-[10px] tracking-[0.28em] uppercase text-ink/70 hover:text-ink transition-colors"
                    >
                      {copiedId === a.id ? "Copied" : "Copy URL"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(a)}
                      disabled={deletingId === a.id}
                      data-testid={`asset-${a.id}-delete`}
                      className="font-sans text-[10px] tracking-[0.28em] uppercase text-red-600/70 hover:text-red-600 disabled:opacity-40 transition-colors"
                      aria-label={`Delete ${a.filename}`}
                    >
                      {deletingId === a.id ? "…" : "Delete"}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Lightbox preview modal — click backdrop or ESC to close. */}
      {preview && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="asset-preview-modal"
          className="fixed inset-0 z-[80] bg-ink/90 flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setPreview(null);
          }}
          tabIndex={-1}
        >
          <div
            className="relative max-w-[95vw] max-h-[90vh] flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={preview.url}
              alt={preview.filename}
              className="max-w-full max-h-[80vh] object-contain"
              data-testid="asset-preview-image"
            />
            <div className="text-paper font-sans text-[11px] tracking-[0.24em] uppercase text-center">
              {preview.filename}
              {preview.width && preview.height ? ` · ${preview.width}×${preview.height}` : ""}
              {preview.size ? ` · ${formatBytes(preview.size)}` : ""}
            </div>
            <button
              type="button"
              onClick={() => setPreview(null)}
              data-testid="asset-preview-close"
              className="text-paper font-sans text-[10px] tracking-[0.32em] uppercase border border-paper/40 hover:border-paper px-4 py-2 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
